import { useState } from "react";
import { GlassCard, GradientText, ScreenShell, SimHeader } from "./shared";
import { ReflectionFigure } from "./decor";

const PROMPTS = [
  { icon:"💬", label:"I'll speak up sooner when…",       starter:"I'll speak up sooner when " },
  { icon:"🔄", label:"I'll challenge my assumption that…", starter:"I'll challenge my assumption that " },
  { icon:"🎯", label:"One habit I'll change is…",         starter:"One habit I'll change is " },
];

export function ReflectionScreen({
  question = "What will you do differently after today?",
  initialValue = "",
  xp = 0,
  minChars = 0,
  onSkip,
  onContinue,
}: {
  question?: string;
  initialValue?: string;
  xp?: number;
  minChars?: number;
  onSkip?: () => void;
  onContinue?: (text: string) => void;
}) {
  const [value, setValue] = useState(initialValue);
  const showSparkle = value.length > 30;
  const meetsMin = minChars <= 0 || value.length >= minChars;
  const charsToGo = Math.max(0, minChars - value.length);

  return (
    <ScreenShell>
      <SimHeader decision={10} xp={xp} />
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-10 md:grid-cols-[200px_1fr] md:px-8">
        {/* LEFT sidebar cards */}
        <aside className="hidden md:flex md:flex-col md:gap-3">
          <GlassCard className="p-4">
            <div className="text-[11px] italic leading-[1.5] text-white/45">
              "We do not learn from experience. We learn from reflecting on experience."
            </div>
            <div className="mt-2 text-[9px] uppercase tracking-[0.18em] text-white/30">— John Dewey</div>
          </GlassCard>
          <GlassCard className="p-4">
            <div className="mb-1 text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color:"#A78BFA" }}>🔒 Private</div>
            <div className="text-[11px] leading-[1.5] text-white/55">Your facilitator will not see this response.</div>
          </GlassCard>
          <GlassCard className="p-3"><ReflectionFigure /></GlassCard>
        </aside>

        {/* MAIN */}
        <div className="relative mx-auto flex w-full max-w-2xl flex-col gap-5">
          <div className="pointer-events-none absolute -left-2 -top-10 select-none font-serif leading-none"
            style={{ fontSize:120, color:"rgba(167,139,250,0.18)" }}>"</div>

          <div className="relative text-center">
            <h1 className="text-[28px] font-black leading-tight">
              <GradientText>One Last Thought</GradientText>
            </h1>
            <p className="mt-2 text-[16px] text-white/55">{question}</p>
          </div>

          <div className="flex flex-wrap justify-center gap-2">
            {PROMPTS.map((p) => (
              <button key={p.label} onClick={() => setValue(p.starter)}
                className="rounded-full border px-3 py-1.5 text-[12px] text-white/75 transition-all hover:-translate-y-0.5 hover:bg-white/10"
                style={{ background:"rgba(255,255,255,0.04)", borderColor:"rgba(167,139,250,0.3)" }}>
                <span className="mr-1">{p.icon}</span> {p.label}
              </button>
            ))}
          </div>

          <div className="relative">
            <textarea value={value} onChange={(e) => setValue(e.target.value)}
              placeholder="Start writing — this is private and only for you…"
              className="min-h-[180px] w-full resize-y rounded-2xl border p-4 text-[15px] leading-[1.6] text-white outline-none transition-all select-text"
              style={{ background:"rgba(255,255,255,0.04)", borderColor:"rgba(255,255,255,0.08)", fontStyle:value?"normal":"italic", WebkitUserSelect:"text", userSelect:"text" }}
              onFocus={(e) => { e.currentTarget.style.borderColor="rgba(139,92,246,0.6)"; e.currentTarget.style.boxShadow="0 0 0 4px rgba(139,92,246,0.12)"; }}
              onBlur={(e) => { e.currentTarget.style.borderColor="rgba(255,255,255,0.08)"; e.currentTarget.style.boxShadow="none"; }} />
            {showSparkle && (
              <div className="pointer-events-none absolute right-3 top-3 text-[18px]"
                style={{ animation:"halo-spin 6s linear infinite", filter:"drop-shadow(0 0 6px rgba(167,139,250,0.6))" }}>✨</div>
            )}
            <div className="absolute bottom-3 right-4 text-[11px] text-white/35">
              {value.length} chars{minChars > 0 ? ` · min ${minChars}` : ""}
            </div>
          </div>

          {minChars > 0 && !meetsMin && (
            <div className="text-center text-[11px] text-amber-300/80">
              {charsToGo} more character{charsToGo === 1 ? "" : "s"} to continue
            </div>
          )}

          <div className="text-center text-[11px] text-white/35">🔒 This reflection is private — for your growth only</div>

          <div className="flex items-center justify-between">
            {onSkip !== undefined ? (
              <button onClick={onSkip} className="text-[14px] text-white/40 hover:text-white/70">Skip</button>
            ) : (
              <span />
            )}
            <button
              onClick={() => { if (meetsMin) onContinue?.(value); }}
              disabled={!meetsMin}
              className="whitespace-nowrap rounded-2xl px-6 py-3 text-[15px] font-bold text-white transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
              style={{ background:"linear-gradient(90deg, #7C3AED, #0EA5E9)", boxShadow:"0 4px 24px rgba(124,58,237,0.4)" }}>
              Continue →
            </button>
          </div>
        </div>
      </div>
    </ScreenShell>
  );
}
